/**
 * Constelación del directorio MCP.
 *
 * MEJORA PROGRESIVA, no un requisito: el directorio ya está renderizado en el
 * HTML. Si no hay WebGL, si el viewport es pequeño o si no hay datos, este
 * módulo se retira en silencio y no pasa nada.
 *
 * prefers-reduced-motion es distinto: pide MENOS MOVIMIENTO, no menos
 * información, así que el canvas se sigue pintando -- un único fotograma
 * quieto, sin deriva, sin pulso y sin la animación de entrada (ver
 * render()/boot() más abajo).
 *
 * WebGL crudo con point sprites y mezcla aditiva: ~8 KB frente a los ~600 KB de
 * Three.js para pintar 1.900 puntos. Las posiciones vienen precalculadas del
 * servidor: aquí no hay simulación de fuerzas.
 */

const FULL_WIDTH = 1024;   // por encima: todos los nodos
const LITE_WIDTH = 768;    // entre ambos: los 300 de mayor ranking
const LITE_NODES = 300;

/**
 * Idioma del documento para formatear numeros. El layout pone
 * lang="{{ app()->getLocale() }}" en <html>, asi que "1939" sale 1.939 en
 * castellano y catalan, 1 939 en frances y 1,939 en ingles. Fijarlo a 'en-US'
 * (como estaba) deja el numero en formato ingles en las cuatro lenguas, y
 * ademas PISA el formato correcto que ya venia del servidor en cuanto termina
 * la animacion de conteo.
 */
const DOC_LOCALE = document.documentElement.lang || 'es';
const fmtNum = (n) => Number(n || 0).toLocaleString(DOC_LOCALE);

const HEALTH_ALPHA = {
    thriving: 1.0,
    active: 0.8,
    dormant: 0.45,
    archived: 0.22,
    deprecated: 0.22,
    unknown: 0.35,
};

const VERT = `
attribute vec2 aPos;
attribute float aSize;
attribute vec3 aColor;
attribute float aAlpha;
uniform float uTime;
uniform float uScale;
uniform float uDpr;
uniform float uStatic;
varying vec3 vColor;
varying float vAlpha;
void main() {
    // Deriva lenta: cada punto respira con una fase distinta. Con
    // prefers-reduced-motion (uStatic=1) no hay deriva: uStatic apaga el
    // término por completo, no solo lo congela en un valor arbitrario.
    float phase = aPos.x * 7.0 + aPos.y * 11.0;
    vec2 drift = uStatic > 0.5 ? vec2(0.0) : vec2(sin(uTime * 0.18 + phase), cos(uTime * 0.15 + phase)) * 0.006;
    gl_Position = vec4((aPos + drift) * uScale, 0.0, 1.0);
    // gl_PointSize se mide en píxeles del FRAMEBUFFER, que resize() deja a
    // dpr veces más denso que la caja CSS del canvas (para que no salga
    // borroso en pantallas HiDPI). Sin multiplicar aquí por uDpr, ese mismo
    // tamaño en píxeles de framebuffer se pinta más pequeño en píxeles CSS
    // cuanto mayor es dpr (a dpr=2, la mitad de grande) — los puntos
    // quedarían diminutos en portátiles/móviles Retina.
    // Mismo criterio que la deriva: con uStatic no hay pulso, tamaño fijo.
    float pulse = uStatic > 0.5 ? 1.0 : (1.0 + 0.06 * sin(uTime * 1.2 + phase));
    gl_PointSize = aSize * uDpr * pulse;
    vColor = aColor;
    vAlpha = aAlpha;
}`;

const FRAG = `
precision mediump float;
varying vec3 vColor;
varying float vAlpha;
void main() {
    // Disco suave: el borde se desvanece para que el punto brille.
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float glow = smoothstep(0.5, 0.0, d);
    gl_FragColor = vec4(vColor * glow, glow * vAlpha);
}`;

function hexToRgb(hex) {
    const n = parseInt((hex || '#38bdf8').slice(1), 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(sh) || 'shader');
    }
    return sh;
}

function boot(host) {
    // prefers-reduced-motion pide MENOS MOVIMIENTO, no menos información:
    // se sigue pintando la constelación, pero como un único fotograma
    // quieto (ver render()). Lo único que sigue apagando el canvas entero
    // son las otras puertas: viewport pequeño, sin WebGL o sin datos.
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (window.innerWidth < LITE_WIDTH) return;

    const canvas = host.querySelector('.mcp-hero__canvas');
    const src = host.dataset.src;
    if (!canvas || !src) return;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: true });
    if (!gl) return;

    fetch(src, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data) => render(host, canvas, gl, data, reduced))
        .catch(() => { /* silencio: el HTML de debajo ya sirve */ });
}

function render(host, canvas, gl, data, reduced) {
    const palette = {};
    (data.categories || []).forEach((c) => { palette[c.slug] = hexToRgb(c.color); });

    let nodes = data.nodes || [];
    if (window.innerWidth < FULL_WIDTH) nodes = nodes.slice(0, LITE_NODES);
    if (!nodes.length) return;

    host.classList.add('is-live');

    const n = nodes.length;
    const pos = new Float32Array(n * 2);
    const size = new Float32Array(n);
    const color = new Float32Array(n * 3);
    const alpha = new Float32Array(n);
    const baseAlpha = new Float32Array(n);

    nodes.forEach((node, i) => {
        pos[i * 2] = node.x;
        pos[i * 2 + 1] = node.y;
        size[i] = 3.0 + node.r * 17.0;
        const rgb = palette[node.c] || [0.22, 0.74, 0.97];
        color[i * 3] = rgb[0];
        color[i * 3 + 1] = rgb[1];
        color[i * 3 + 2] = rgb[2];
        baseAlpha[i] = HEALTH_ALPHA[node.h] ?? 0.5;
        alpha[i] = baseAlpha[i];
    });

    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buffers = {};
    const bind = (name, arr, comps, dynamic) => {
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, arr, dynamic ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW);
        const loc = gl.getAttribLocation(prog, name);
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, comps, gl.FLOAT, false, 0, 0);
        buffers[name] = buf;
    };

    bind('aPos', pos, 2, false);
    bind('aSize', size, 1, false);
    bind('aColor', color, 3, false);
    bind('aAlpha', alpha, 1, true);

    const uTime = gl.getUniformLocation(prog, 'uTime');
    const uScale = gl.getUniformLocation(prog, 'uScale');
    const uDpr = gl.getUniformLocation(prog, 'uDpr');
    const uStatic = gl.getUniformLocation(prog, 'uStatic');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // aditiva: los cúmulos brillan más
    gl.uniform1f(uStatic, reduced ? 1.0 : 0.0);

    const resize = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = canvas.clientWidth * dpr;
        canvas.height = canvas.clientHeight * dpr;
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.uniform1f(uDpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // Un único pintado, reusado tanto por el modo estático como por cada
    // fotograma del modo animado. uScale en 1.0 es su valor final (el mismo
    // al que llega la animación de entrada cuando age->1).
    const FINAL_SCALE = 1.0;
    // El hit-test de hover/clic (más abajo) necesita saber a qué escala se
    // pintó el ÚLTIMO fotograma para convertir coordenadas -- en modo
    // animado uScale cambia en cada frame (0.25 -> 1.0 durante el primer
    // segundo), así que se recuerda aquí en vez de asumir FINAL_SCALE.
    let currentScale = reduced ? FINAL_SCALE : 0.25;
    const draw = (time, scale) => {
        currentScale = scale;
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform1f(uTime, time);
        gl.uniform1f(uScale, scale);
        gl.drawArrays(gl.POINTS, 0, n);
    };

    // Búsqueda: apaga lo que no casa. Sin recargar, sin tocar el servidor.
    const input = host.querySelector('[data-mcp-filter]');
    if (input) {
        let timer = null;
        input.addEventListener('input', () => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                const q = input.value.trim().toLowerCase();
                for (let i = 0; i < n; i++) {
                    const hit = !q || (nodes[i].t || '').toLowerCase().includes(q);
                    alpha[i] = hit ? baseAlpha[i] : 0.05;
                }
                gl.bindBuffer(gl.ARRAY_BUFFER, buffers.aAlpha);
                gl.bufferSubData(gl.ARRAY_BUFFER, 0, alpha);
                // En modo estático no hay bucle de rAF que recoja este cambio
                // en el siguiente fotograma -- no hay siguiente fotograma.
                // Hay que repintar el único que existe a mano.
                if (reduced) draw(0, FINAL_SCALE);
            }, 120);
        });
    }

    // ---- Interacción: hover con tooltip + clic a la ficha -----------------
    // El campo `u` de cada nodo (su URL) viaja en el JSON desde el hallazgo
    // I4 pero nadie lo leía todavía; aquí es lo que resuelve el clic.
    //
    // Rejilla espacial para el hover: ~1.900 nodos y el mousemove dispara sin
    // parar -- recorrerlos todos en cada evento no es viable. Las posiciones
    // (`node.x/y`) las manda el servidor ya calculadas y NUNCA cambian, así
    // que la rejilla se construye UNA sola vez sobre ese espacio "raw" (sin
    // escalar, sin la deriva del shader) y sirve toda la vida de la página:
    // ni el resize ni la animación de entrada (uScale variando) la invalidan.
    {
        const GRID = 40; // celdas por eje sobre el dominio [-1,1] del servidor
        const cellIndex = (v) => Math.min(GRID - 1, Math.max(0, Math.floor((v + 1) * 0.5 * GRID)));
        const grid = new Array(GRID * GRID);
        nodes.forEach((node, i) => {
            const key = cellIndex(node.x) * GRID + cellIndex(node.y);
            (grid[key] || (grid[key] = [])).push(i);
        });

        // clip space (raw, sin escalar) -> píxeles CSS de la caja del canvas.
        // OJO con el eje Y: WebGL tiene +1 arriba, CSS tiene +Y hacia abajo,
        // así que se invierte. El devicePixelRatio NO entra en esta cuenta a
        // propósito: gl_PointSize ya se multiplica por uDpr en el shader
        // exactamente para que `size[i]` (más abajo) sea el diámetro visual
        // en píxeles CSS SIN importar el dpr, y la proyección de posición
        // (canvas.width/dpr === canvas.clientWidth) cancela el dpr igual.
        // Verificado a mano con dpr=1 y dpr=2 antes de dejarlo así.
        const nodeToCss = (node, scale, rect) => [
            (node.x * scale * 0.5 + 0.5) * rect.width,
            (1 - (node.y * scale * 0.5 + 0.5)) * rect.height,
        ];

        // La deriva (drift) del vertex shader no entra en esta cuenta: su
        // amplitud máxima son 0.006 unidades de clip space -- unos pocos
        // píxeles como mucho -- y ya cabe dentro de la holgura de HIT_PAD.
        const HIT_PAD = 4; // px CSS de holgura extra sobre el radio del punto
        const MAX_RADIUS_CSS = 20 / 2 + HIT_PAD; // el punto más grande posible

        const findNodeAt = (cssX, cssY, rect) => {
            const scale = currentScale;
            // Inversa de nodeToCss: de píxeles CSS del cursor al mismo
            // espacio "raw" en el que vive la rejilla.
            const rawX = (2 * (cssX / rect.width) - 1) / scale;
            const rawY = (1 - 2 * (cssY / rect.height)) / scale;

            // Cuántos anillos de celdas vecinas mirar depende de uScale: a
            // escala final (1.0, el caso normal -- y el único que existe en
            // modo estático) la holgura de un puñado de píxeles cabe de sobra
            // en una celda y un anillo (3x3) basta. Con uScale más pequeño
            // (arranque de la animación de entrada, 0.25) ese mismo radio en
            // píxeles ocupa más celdas -- se ensancha la búsqueda en vez de
            // fingir que el problema no existe.
            const cell = 2 / GRID;
            const tolRawX = (MAX_RADIUS_CSS * 2) / (scale * rect.width);
            const tolRawY = (MAX_RADIUS_CSS * 2) / (scale * rect.height);
            const ring = Math.max(1, Math.ceil(Math.max(tolRawX, tolRawY) / cell));

            const cx = cellIndex(rawX);
            const cy = cellIndex(rawY);
            let best = -1;
            let bestDist = Infinity;

            for (let dx = -ring; dx <= ring; dx++) {
                const gx = cx + dx;
                if (gx < 0 || gx >= GRID) continue;
                for (let dy = -ring; dy <= ring; dy++) {
                    const gy = cy + dy;
                    if (gy < 0 || gy >= GRID) continue;
                    const bucket = grid[gx * GRID + gy];
                    if (!bucket) continue;
                    for (const i of bucket) {
                        const [px, py] = nodeToCss(nodes[i], scale, rect);
                        const ddx = px - cssX;
                        const ddy = py - cssY;
                        const dist = Math.sqrt(ddx * ddx + ddy * ddy);
                        const radius = size[i] / 2 + HIT_PAD;
                        if (dist <= radius && dist < bestDist) {
                            best = i;
                            bestDist = dist;
                        }
                    }
                }
            }
            return best;
        };

        // Tooltip: reusa el CSS ya escrito (.mcp-hero__tooltip/.is-visible),
        // no se define ninguna clase nueva. Se crea por JS -- es decorativo,
        // parte de la mejora progresiva del canvas, igual que el propio
        // canvas -- y se cuelga de `host` (.mcp-hero, position:relative),
        // que es exactamente la misma caja de referencia que el canvas
        // (position:absolute; inset:0), así que cssX/cssY sirven tal cual
        // como left/top.
        const tooltip = document.createElement('div');
        tooltip.className = 'mcp-hero__tooltip';
        tooltip.setAttribute('aria-hidden', 'true');
        host.appendChild(tooltip);

        let lastHoverIdx = -1;
        let tooltipW = 0;
        let tooltipH = 0;

        const fillTooltip = (node) => {
            tooltip.textContent = '';
            const dot = document.createElement('span');
            dot.className = 'mcp-health mcp-health--' + (node.h || 'unknown');
            const name = document.createElement('strong');
            name.textContent = node.t || '';
            const stars = document.createElement('span');
            // 'en-US' fijo a propósito, no el locale del navegador: es el
            // mismo formato (coma cada 3 dígitos) que number_format() de PHP
            // pinta en el resto de la página (mcp-card__meta, trending...),
            // que tampoco es locale-aware -- así el tooltip no desentona con
            // un separador distinto según el idioma del navegador de quien
            // visita, sea cual sea el idioma de la propia página.
            stars.textContent = ' · ' + Number(node.s || 0).toLocaleString(DOC_LOCALE) + ' ★';
            tooltip.appendChild(dot);
            tooltip.appendChild(name);
            tooltip.appendChild(stars);
        };

        const positionTooltip = (cssX, cssY, rect) => {
            let left = cssX + 14;
            let top = cssY + 14;
            if (left + tooltipW > rect.width) left = cssX - 14 - tooltipW;
            if (top + tooltipH > rect.height) top = cssY - 14 - tooltipH;
            tooltip.style.left = Math.max(0, left) + 'px';
            tooltip.style.top = Math.max(0, top) + 'px';
        };

        const hideTooltip = () => {
            lastHoverIdx = -1;
            tooltip.classList.remove('is-visible');
            canvas.style.cursor = '';
        };

        canvas.addEventListener('mousemove', (ev) => {
            const rect = canvas.getBoundingClientRect();
            const cssX = ev.clientX - rect.left;
            const cssY = ev.clientY - rect.top;
            const idx = findNodeAt(cssX, cssY, rect);

            if (idx === -1) {
                hideTooltip();
                return;
            }

            const node = nodes[idx];
            // Solo se ve "clicable" si de verdad lo es: ~12 nodos sin
            // categoría no tienen `u` y el clic no hace nada sobre ellos.
            canvas.style.cursor = node.u ? 'pointer' : '';
            if (idx !== lastHoverIdx) {
                lastHoverIdx = idx;
                fillTooltip(node);
                tooltip.classList.add('is-visible');
                tooltipW = tooltip.offsetWidth;
                tooltipH = tooltip.offsetHeight;
            }
            positionTooltip(cssX, cssY, rect);
        });

        canvas.addEventListener('mouseleave', hideTooltip);

        canvas.addEventListener('click', (ev) => {
            const rect = canvas.getBoundingClientRect();
            const idx = findNodeAt(ev.clientX - rect.left, ev.clientY - rect.top, rect);
            if (idx === -1) return;
            const url = nodes[idx].u;
            if (url) window.location.href = url;
        });
    }

    if (reduced) {
        // Estático: un solo fotograma, sin requestAnimationFrame en bucle,
        // con uScale ya en su valor final (sin animación de entrada). La
        // deriva y el pulso quedan apagados en el shader vía uStatic.
        draw(0, FINAL_SCALE);
        return;
    }

    const t0 = performance.now();
    let raf = null;

    const frame = () => {
        const elapsed = performance.now() - t0;
        // Entrada: los puntos se despliegan desde el centro en el primer segundo.
        const age = Math.min(elapsed / 900, 1);
        draw(elapsed / 1000, 0.25 + 0.75 * (1 - Math.pow(1 - age, 3)));
        raf = requestAnimationFrame(frame);
    };
    frame();

    // No malgastar GPU en una pestaña que nadie mira.
    // OJO: algunos navegadores/SO disparan 'visibilitychange' dos veces seguidas
    // al volver a primer plano. Sin guarda, cada disparo de más llamaría a
    // frame() directamente, y como frame() siempre encadena su propio
    // requestAnimationFrame al final, cada llamada extra arrancaría OTRO bucle
    // en paralelo — y como `raf` solo recuerda el último id devuelto, los
    // bucles anteriores quedan huérfanos: cancelAnimationFrame ya no puede
    // pararlos ni siquiera cuando la pestaña vuelve a ocultarse. El guard
    // `raf === null` asegura que solo arranca un bucle nuevo si no hay ya uno
    // vivo, así dos disparos seguidos del evento son inofensivos.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            cancelAnimationFrame(raf);
            raf = null;
        } else if (raf === null) {
            frame();
        }
    });
}

/**
 * Contadores animados del hero (data-count-to, Bloque 3): cuentan hacia
 * arriba al entrar en pantalla, no al cargar -- si el hero nace fuera de
 * viewport (pantallas bajas, hero recortado) no tiene sentido gastar frames
 * en algo que nadie ve todavía.
 *
 * prefers-reduced-motion: la preferencia quita ANIMACIÓN, no contenido
 * (mismo criterio que boot()/render() de arriba). El valor final YA está en
 * el HTML servido (SSR) -- con esa preferencia esta función no toca el DOM
 * en absoluto: no hay ningún "salto" que evitar porque nunca hay nada que
 * mover, el número correcto es el único que ha existido siempre.
 */
function wireCounters() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const counters = Array.from(document.querySelectorAll('[data-count-to]'));
    const hero = document.querySelector('.mcp-hero');
    if (!counters.length || !hero || !('IntersectionObserver' in window)) return;

    const DURATION = 1100;

    const animate = (el) => {
        const target = parseInt(el.dataset.countTo, 10);
        if (!Number.isFinite(target)) return;

        const t0 = performance.now();
        const step = (now) => {
            const age = Math.min((now - t0) / DURATION, 1);
            // Mismo ease-out cúbico que la entrada de la constelación (age->1
            // en render()): un solo lenguaje de movimiento en todo el hero.
            const eased = 1 - Math.pow(1 - age, 3);
            if (age < 1) {
                el.textContent = Math.round(target * eased).toLocaleString(DOC_LOCALE);
                requestAnimationFrame(step);
            } else {
                // Valor exacto al terminar -- no arrastra el redondeo del
                // último frame, coincide con lo que el servidor ya pintó.
                el.textContent = target.toLocaleString(DOC_LOCALE);
            }
        };
        requestAnimationFrame(step);
    };

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            counters.forEach(animate);
            io.disconnect();
        });
    }, { threshold: 0.3 });

    io.observe(hero);
}

/**
 * Buscador global del hero (Bloque 4): resultados instantáneos sobre TODO el
 * catálogo (~1.900 servidores), no solo los 24 de "Destacados" -- ese
 * filtrado de tarjetas y el "modo búsqueda" que ocultaba paneles (clase
 * .is-searching) se han retirado enteros, ver control_room.blade.php e
 * index.blade.php.
 *
 * Índice ligero (search-index.json: nombre+url+categoría+estrellas, SIN
 * posiciones ni colores -- eso es constellation.json) cargado UNA vez al
 * arrancar este script y filtrado en cliente, sin ida y vuelta al servidor
 * por cada tecla. Se pide TAMBIÉN en móvil, a diferencia de
 * constellation.json (que boot() ni intenta por debajo de LITE_WIDTH): no
 * hay "busca solo lo que ves" en pantalla pequeña, el catálogo es el mismo
 * en cualquier viewport.
 *
 * Patrón ARIA combobox (WAI-ARIA 1.2): el foco real nunca sale del <input>
 * -- la fila resaltada se comunica con aria-activedescendant + una clase
 * visual (highlight()), no moviendo el foco del navegador por el DOM. Los
 * atributos de combobox se añaden aquí, no en el Blade: si este script no
 * llega a ejecutarse, un lector de pantalla no debe anunciar un control que
 * no va a responder.
 *
 * Sin JS, el <form> alrededor del input ya apunta a
 * McpServerController::search() (index.blade.php): Enter o el botón buscan
 * en el catálogo entero igual, solo que en una página aparte en vez de en
 * este desplegable.
 */
function wireHeroSearch() {
    const root = document.querySelector('[data-mcp-search]');
    const input = document.querySelector('[data-mcp-filter]');
    const results = document.querySelector('[data-mcp-search-results]');
    const status = document.querySelector('[data-mcp-search-status]');
    if (!root || !input || !results || !root.dataset.src) return;

    const MAX_SHOWN = 8;

    // Progresivo: el combobox solo se anuncia si el JS arrancó de verdad.
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', results.id || 'mcp-search-listbox');
    input.setAttribute('aria-autocomplete', 'list');

    const countOne = status ? (status.dataset.tplCountOne || '') : '';
    const countOther = status ? (status.dataset.tplCountOther || '') : '';
    const tplEmpty = status ? (status.dataset.tplEmpty || '') : '';
    const tplViewAll = results.dataset.tplViewAll || '';
    const searchUrl = results.dataset.searchUrl || '';

    const setStatus = (text) => { if (status) status.textContent = text || ''; };

    let items = null; // null = el índice aún no ha llegado
    let categories = {};

    fetch(root.dataset.src, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((data) => {
            items = data.items || [];
            categories = data.categories || {};
            // Si el usuario ya escribió algo mientras el índice viajaba, no se
            // pierde: se busca ahora que hay con qué.
            const q = input.value.trim();
            if (q) search(q);
        })
        .catch(() => { items = []; }); // silencio: sin índice, el <form> de toda la vida sigue sirviendo

    let active = -1;    // posición dentro de `painted`, -1 = ninguna fila resaltada
    let painted = [];   // [{id, href}] de las filas pintadas ahora mismo, en orden

    const close = () => {
        results.hidden = true;
        results.textContent = '';
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        active = -1;
        painted = [];
    };

    const highlight = (idx) => {
        painted.forEach((p, i) => {
            const el = document.getElementById(p.id);
            if (!el) return;
            const isActive = i === idx;
            el.classList.toggle('is-active', isActive);
            el.setAttribute('aria-selected', String(isActive));
        });
        active = idx;
        if (idx >= 0 && painted[idx]) {
            input.setAttribute('aria-activedescendant', painted[idx].id);
        } else {
            input.removeAttribute('aria-activedescendant');
        }
    };

    const renderResults = (matches, query) => {
        results.textContent = '';
        painted = [];

        if (!matches.length) {
            const empty = document.createElement('p');
            empty.className = 'mcp-search-result mcp-search-result--empty';
            empty.textContent = tplEmpty ? tplEmpty.replace(':query', query) : '';
            results.appendChild(empty);
            results.hidden = false;
            input.setAttribute('aria-expanded', 'true');
            setStatus(empty.textContent);
            return;
        }

        const shown = matches.slice(0, MAX_SHOWN);
        const frag = document.createDocumentFragment();

        shown.forEach((m, i) => {
            const id = 'mcp-search-opt-' + i;
            const row = document.createElement('div');
            row.className = 'mcp-search-result';
            row.id = id;
            row.setAttribute('role', 'option');
            row.setAttribute('aria-selected', 'false');

            const a = document.createElement('a');
            a.href = m.u;
            a.tabIndex = -1; // el foco real se queda en el <input>, patrón combobox

            const name = document.createElement('span');
            name.className = 'mcp-search-result__name';
            name.textContent = m.n;

            const meta = document.createElement('span');
            meta.className = 'mcp-search-result__meta';
            const catName = categories[m.c] || m.c || '';
            // 'en-US' fijo a propósito: mismo formato que number_format() de
            // PHP pinta en el resto de la página (ver tooltip del canvas,
            // más arriba en este mismo archivo).
            meta.textContent = catName + ' · ' + Number(m.s || 0).toLocaleString(DOC_LOCALE) + ' ★';

            a.appendChild(name);
            a.appendChild(meta);
            row.appendChild(a);
            frag.appendChild(row);
            painted.push({ id, href: m.u });
        });

        if (matches.length > shown.length && tplViewAll && searchUrl) {
            // Misma forma que las filas normales (div.mcp-search-result[role=option]
            // > a) a propósito, no un <a> suelto: así una única regla CSS
            // (.mcp-search-result a) cubre el aspecto de fila de las dos, sin
            // duplicar selectores para un caso especial.
            const id = 'mcp-search-opt-more';
            const row = document.createElement('div');
            row.className = 'mcp-search-result mcp-search-result--more';
            row.id = id;
            row.setAttribute('role', 'option');
            row.setAttribute('aria-selected', 'false');

            const a = document.createElement('a');
            a.href = searchUrl + '?q=' + encodeURIComponent(query);
            a.tabIndex = -1;
            a.textContent = tplViewAll.replace(':count', String(matches.length));

            row.appendChild(a);
            frag.appendChild(row);
            painted.push({ id, href: a.href });
        }

        results.appendChild(frag);
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        highlight(-1);

        const tpl = matches.length === 1 ? countOne : countOther;
        setStatus(tpl ? tpl.replace(':count', String(matches.length)) : '');
    };

    function search(query) {
        if (!query) {
            close();
            setStatus('');
            return;
        }
        if (items === null) return; // índice aún no ha llegado: nada que enseñar todavía

        const q = query.toLowerCase();
        const matches = items.filter((it) => (it.n || '').toLowerCase().includes(q));
        renderResults(matches, query);
    }

    let timer = null;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        timer = setTimeout(() => search(q), 150);
    });

    input.addEventListener('keydown', (ev) => {
        if (results.hidden && ev.key !== 'ArrowDown') return;

        if (ev.key === 'ArrowDown') {
            ev.preventDefault();
            if (results.hidden) { search(input.value.trim()); return; }
            highlight(active + 1 >= painted.length ? 0 : active + 1);
        } else if (ev.key === 'ArrowUp') {
            ev.preventDefault();
            highlight(active - 1 < 0 ? painted.length - 1 : active - 1);
        } else if (ev.key === 'Escape') {
            close();
        } else if (ev.key === 'Enter') {
            if (active >= 0 && painted[active]) {
                // Con una fila resaltada por teclado, Enter navega a ESA
                // ficha en vez de enviar el <form> -- sin fila activa, se
                // deja el envío normal (McpServerController::search()).
                ev.preventDefault();
                window.location.href = painted[active].href;
            }
        }
    });

    // Cierre al hacer click fuera o al salir por Tab -- relatedTarget es
    // quien RECIBE el foco; si sigue dentro de `root` (p.ej. el botón de
    // enviar), no se cierra.
    document.addEventListener('click', (ev) => {
        if (!root.contains(ev.target)) close();
    });
    root.addEventListener('focusout', (ev) => {
        if (!root.contains(ev.relatedTarget)) close();
    });
}

wireCounters();
wireHeroSearch();
document.querySelectorAll('[data-mcp-constellation]').forEach(boot);
